import { useEffect, useState } from 'react';
import {
  Button, Text, Badge,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
  makeStyles, tokens,
} from '@fluentui/react-components';
import { useT } from '../../i18n';
import { apiErrorMessage } from '../../api/apiError';
import {
  fetchComplianceTemplate, previewComplianceImport, confirmComplianceImport,
  type ComplianceImportPreview, type ComplianceImportResult,
} from '../../api/compliance-rules.api';

const useStyles = makeStyles({
  body:  { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: '320px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: tokens.fontSizeBase200 },
  cell:  { padding: '4px 8px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, textAlign: 'left', verticalAlign: 'top' },
  scroll: { maxHeight: '320px', overflowY: 'auto', overflowX: 'auto' },
  error: { color: tokens.colorPaletteRedForeground1, fontSize: tokens.fontSizeBase200 },
  ok:    { color: tokens.colorPaletteGreenForeground1 },
});

function fill(template: string, vars: Record<string, string | number>): string {
  return Object.entries(vars).reduce((acc, [k, v]) => acc.split(`{${k}}`).join(String(v)), template);
}

export interface ComplianceImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImported: (result: ComplianceImportResult) => void;
}

export function ComplianceImportDialog({ open, onClose, onImported }: ComplianceImportDialogProps) {
  const s = useStyles();
  const t = useT();
  const c = t.complianceRules;
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ComplianceImportPreview | null>(null);
  const [result, setResult] = useState<ComplianceImportResult | null>(null);
  const [busy, setBusy] = useState<'preview' | 'confirm' | null>(null);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (open) { setFile(null); setPreview(null); setResult(null); setError(undefined); setBusy(null); }
  }, [open]);

  const download = async () => {
    try {
      const { fileName, fileBase64 } = await fetchComplianceTemplate();
      const bytes = Uint8Array.from(atob(fileBase64), (ch) => ch.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url; a.download = fileName; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(apiErrorMessage(e, c.errorGeneric));
    }
  };

  const runPreview = async () => {
    if (!file) return;
    setBusy('preview'); setError(undefined); setResult(null);
    try { setPreview(await previewComplianceImport(file)); }
    catch (e) { setPreview(null); setError(apiErrorMessage(e, c.errorGeneric)); }
    finally { setBusy(null); }
  };

  const runConfirm = async () => {
    if (!file) return;
    setBusy('confirm'); setError(undefined);
    try {
      const r = await confirmComplianceImport(file);
      setResult(r);
      onImported(r);
    } catch (e) { setError(apiErrorMessage(e, c.errorGeneric)); }
    finally { setBusy(null); }
  };

  const canConfirm = !!preview && preview.summary.errors === 0 && preview.summary.valid > 0 && !result;

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) onClose(); }}>
      <DialogSurface style={{ maxWidth: '900px' }}>
        <DialogBody>
          <DialogTitle>{c.importTitle}</DialogTitle>
          <DialogContent>
            <div className={s.body}>
              <Text>{c.importHelp}</Text>
              <div>
                <Button onClick={download}>{c.downloadTemplate}</Button>
              </div>
              <label>
                <Text weight="semibold">{c.chooseFile}</Text>{' '}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  aria-label={c.chooseFile}
                  onChange={(e) => { setFile(e.target.files?.[0] ?? null); setPreview(null); setResult(null); }}
                />
              </label>
              {preview && (
                <>
                  <Text>{fill(c.previewSummary, preview.summary)}</Text>
                  {preview.summary.errors > 0 && <Text className={s.error}>{c.fixErrors}</Text>}
                  <div className={s.scroll}>
                    <table className={s.table}>
                      <thead>
                        <tr>
                          <th className={s.cell}>{c.colRow}</th>
                          <th className={s.cell}>{c.colProduct}</th>
                          <th className={s.cell}>{c.colCrop}</th>
                          <th className={s.cell}>{c.colMarket}</th>
                          <th className={s.cell}>{c.colPhi}</th>
                          <th className={s.cell}>{c.colRei}</th>
                          <th className={s.cell}>{c.colMrl}</th>
                          <th className={s.cell}>{c.colStatus}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.rows.map((r) => (
                          <tr key={r.row}>
                            <td className={s.cell}>{r.row}</td>
                            <td className={s.cell}>{r.productName ?? r.productId ?? '—'}</td>
                            <td className={s.cell}>{r.crop ?? '—'}</td>
                            <td className={s.cell}>{r.market ?? '—'}</td>
                            <td className={s.cell}>{r.phiDays ?? '—'}</td>
                            <td className={s.cell}>{r.reiHours ?? '—'}</td>
                            <td className={s.cell}>{r.mrlValue !== undefined ? `${r.mrlValue} ${r.mrlUnit ?? ''}` : '—'}</td>
                            <td className={s.cell}>
                              {r.errors.length === 0
                                ? <Badge color="success" appearance="tint">{r.action === 'UPDATE' ? c.actionUpdate : c.actionCreate}</Badge>
                                : r.errors.map((e, i) => <div key={i} className={s.error}>{e.message}</div>)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              {result && (
                <Text className={s.ok} role="status">
                  {fill(c.imported, { created: result.created, updated: result.updated })}
                  {result.failed.length > 0 && <> {fill(c.failedRows, { rows: result.failed.join(', ') })}</>}
                </Text>
              )}
              {error && <Text className={s.error} role="alert">{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>{c.close}</Button>
            <Button onClick={runPreview} disabled={!file || busy !== null}>{busy === 'preview' ? c.previewing : c.preview}</Button>
            <Button appearance="primary" onClick={runConfirm} disabled={!canConfirm || busy !== null}>
              {busy === 'confirm' ? c.importing : c.confirmImport}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
